/**
 * Modal component for viewing submission details
 */

import React from 'react';
import { X, Trash2, Mail, Clock, Calendar } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { FormSubmission } from '../../types/submissions';

interface SubmissionDetailModalProps {
  submission: FormSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (submission: FormSubmission) => void;
  isDeleting: boolean;
}

export const SubmissionDetailModal: React.FC<SubmissionDetailModalProps> = ({
  submission,
  isOpen,
  onClose,
  onDelete,
  isDeleting,
}) => {
  if (!isOpen || !submission) return null;

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
  };

  const formData = (submission.formData || {}) as Record<string, unknown>;
  const { date, time } = formatDateTime(submission.submittedAt);

  // Filter out internal fields
  const displayFields = Object.entries(formData).filter(
    ([key]) => !['_honeypot', 'timestamp', 'formId', 'pageId', 'componentId'].includes(key)
  );

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      onDelete(submission);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Submission Details</h2>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {date}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {time}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {/* Submitter email if available */}
            {submission.submitterEmail && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="font-medium">From:</span>
                  <a
                    href={`mailto:${submission.submitterEmail}`}
                    className="ml-2 text-primary-600 hover:underline"
                  >
                    {submission.submitterEmail}
                  </a>
                </div>
              </div>
            )}

            {/* Form fields */}
            <div className="space-y-4">
              {displayFields.map(([key, value]) => (
                <div key={key} className="border-b border-gray-100 pb-4 last:border-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <div className="text-gray-900 whitespace-pre-wrap break-words">
                    {typeof value === 'boolean' ? (
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {value ? 'Yes' : 'No'}
                      </span>
                    ) : Array.isArray(value) ? (
                      <div className="flex flex-wrap gap-1">
                        {value.map((item, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-sm"
                          >
                            {String(item)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      String(value) || <span className="text-gray-400 italic">Empty</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {displayFields.length === 0 && (
              <p className="text-gray-500 text-center py-4">No form data available</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end bg-gray-50">
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className={cn(
                'inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors',
                isDeleting && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
